import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FilterScreenModel from "../FilterScreenModel/FilterScreenModel";
import { toast } from "react-toastify";
import "./filterScreen.css";
import { motion } from "framer-motion";
import {
  ChevronRight,
  ListFilter,
  Search,
  SlidersHorizontal,
  MapPin,
} from "lucide-react";

const tabToPath = (tab) => {
  if (tab === "Rent") return "rent";
  if (tab === "Sell") return "sold";
  if (tab === "Buy") return "buy";
  return "buy";
};

const PLACES_COUNTRY_NG = { country: "ng" };

const pickNigeriaGeocodeResult = (results) => {
  if (!results?.length) return null;
  return (
    results.find((r) =>
      r.address_components?.some(
        (c) =>
          c.types?.includes("country") &&
          String(c.short_name || "").toUpperCase() === "NG"
      )
    ) ?? null
  );
};

const mapsApiKey = () =>
  process.env.REACT_APP_GOOGLE_API_KEY ||
  process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

const loadGoogleMapsOnce = () =>
  new Promise((resolve, reject) => {
    if (window.google?.maps?.places?.AutocompleteService) {
      resolve();
      return;
    }
    const marker = "data-la-rosa-google-maps";
    const existing = document.querySelector(`script[${marker}]`);
    if (existing) {
      if (window.google?.maps?.places?.AutocompleteService) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => {
        if (window.google?.maps?.places?.AutocompleteService) resolve();
        else reject(new Error("Google Maps failed"));
      });
      existing.addEventListener("error", () =>
        reject(new Error("Google Maps failed"))
      );
      return;
    }
    const key = mapsApiKey();
    if (!key) {
      reject(new Error("Missing Google Maps API key"));
      return;
    }
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${key}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.setAttribute(marker, "1");
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Google Maps"));
    document.head.appendChild(script);
  });

const FilterScreen = ({ setSearch }) => {
  const navigate = useNavigate();
  const [IsOpen, setIsOpen] = useState(false);
  const [locationBusy, setLocationBusy] = useState(false);

  const TabData = ["Buy", "Rent", "Sell", "Agents"];
  const Filterable = ["Buy", "Rent", "Sell", "Agents"];
  const [isActive, setisActive] = useState(TabData[0]);

  const [searchValue, setSearchValue] = useState("");
  const [searchAgent, setSearchAgent] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const sessionTokenRef = useRef(null);
  const debounceRef = useRef(null);
  const suggestWrapRef = useRef(null);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = null;
  }, []);

  const ensureSessionToken = useCallback(() => {
    if (!window.google?.maps?.places) return null;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const fetchPlaceSuggestions = useCallback(
    async (input) => {
      const q = input.trim();
      if (q.length < 2) {
        setSuggestions([]);
        setHighlightIndex(-1);
        return;
      }
      try {
        await loadGoogleMapsOnce();
        const svc = new window.google.maps.places.AutocompleteService();
        const token = ensureSessionToken();
        svc.getPlacePredictions(
          {
            input: q,
            sessionToken: token,
            componentRestrictions: PLACES_COUNTRY_NG,
          },
          (predictions, status) => {
            const ok =
              status === window.google.maps.places.PlacesServiceStatus.OK;
            const zero =
              status ===
              window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS;
            if (!ok && !zero) {
              setSuggestions([]);
              setHighlightIndex(-1);
              return;
            }
            setSuggestions(predictions || []);
            setHighlightIndex(-1);
          }
        );
      } catch {
        setSuggestions([]);
        setHighlightIndex(-1);
      }
    },
    [ensureSessionToken]
  );

  useEffect(() => {
    if (!Filterable.includes(isActive) || isActive === "Agents") {
      setSuggestions([]);
      setHighlightIndex(-1);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchPlaceSuggestions(searchValue);
    }, 280);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchValue, isActive, fetchPlaceSuggestions]);

  useEffect(() => {
    const close = (e) => {
      if (
        suggestWrapRef.current &&
        !suggestWrapRef.current.contains(e.target)
      ) {
        setSuggestions([]);
        setHighlightIndex(-1);
      }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const applySuggestion = (prediction) => {
    const text = prediction.description;
    setSearchValue(text);
    localStorage.setItem("search", text);
    setSuggestions([]);
    setHighlightIndex(-1);
    resetSessionToken();
  };

  const runLocationSearch = async () => {
    const pageSlug = tabToPath(isActive);
    setLocationBusy(true);
    setSuggestions([]);
    try {
      await loadGoogleMapsOnce();
      if (!navigator.geolocation) {
        throw new Error("Geolocation not supported");
      }
      const pos = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 60000,
        });
      });
      const lat = pos.coords.latitude;
      const lng = pos.coords.longitude;
      const label = await new Promise((resolve, reject) => {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
          if (status !== "OK" || !results?.length) {
            reject(new Error(status || "Geocode failed"));
            return;
          }
          const ng = pickNigeriaGeocodeResult(results);
          if (!ng?.formatted_address) {
            reject(new Error("NOT_NG"));
            return;
          }
          resolve(ng.formatted_address);
        });
      });
      setSearchValue(label);
      localStorage.setItem("search", label);
      setSearch(label);
      navigate(`/${pageSlug}/searched/${encodeURIComponent(label)}`);
    } catch (err) {
      const denied = err?.code === 1;
      const outsideNg = err?.message === "NOT_NG";
      toast.error(
        denied
          ? "Location access denied. Allow location or enter an area."
          : outsideNg
            ? "Your location is outside Nigeria. Search for a place in Nigeria."
            : "Could not use your location. Try entering a suburb or postcode."
      );
      navigate(`/${pageSlug}`);
    } finally {
      setLocationBusy(false);
    }
  };

  const searchBtn = async () => {
    const pageSlug = tabToPath(isActive);
    const trimmed = searchValue.trim();
    if (trimmed.length === 0) {
      await runLocationSearch();
      return;
    }
    setSearch(trimmed);
    localStorage.setItem("search", trimmed);
    setSuggestions([]);
    resetSessionToken();
    navigate(`/${pageSlug}/searched/${encodeURIComponent(trimmed)}`);
  };

  const localSave = (e) => {
    localStorage.setItem("search", e.target.value);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions.length > 0 && highlightIndex >= 0) {
        applySuggestion(suggestions[highlightIndex]);
      } else {
        void searchBtn();
      }
      return;
    }
    if (!suggestions.length) {
      if (e.key === "Escape") {
        setSuggestions([]);
        setHighlightIndex(-1);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) =>
        i < suggestions.length - 1 ? i + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) =>
        i <= 0 ? suggestions.length - 1 : i - 1
      );
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setHighlightIndex(-1);
    }
  };

  return (
    <>
      <div className="absolute z-2 top-[25%] sm:top-[20%] md:top-[30%] left-0 right-0 w-full flex justify-center px-3 sm:px-4 md:px-6">
        <div className="w-full max-w-[32rem] sm:max-w-4xl">
          <div className="lg:w-[30%] w-full flex gap-4 sm:gap-6 md:gap-8 bg-white rounded-t-xl px-4 sm:px-6 pt-3 sm:pt-4 border-b border-gray-200 overflow-x-auto scrollBarHidden">
            {TabData?.map((d, index) => (
              <button
                key={index}
                onClick={() => {
                  setisActive(d);
                }}
                className="relative pb-3 text-xs sm:text-sm font-medium text-center text-gray-500 hover:text-gray-700 transition whitespace-nowrap flex-1 lg:flex-shrink-0"
              >
                <span className={isActive === d ? "text-[#097FE6]" : ""}>
                  {d}
                </span>
                {isActive === d && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#097FE6] rounded-full"
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                    }}
                  />
                )}
              </button>
            ))}
          </div>

          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3 bg-white px-4 sm:px-6 py-5 sm:py-7 rounded-b-xl shadow-xl rounded-tr-none lg:rounded-tr-xl">
            {Filterable?.includes(isActive) && isActive !== "Agents" && (
              <>
                <div className="flex flex-col md:flex-row gap-3 flex-1 w-full min-w-0">
                  <div
                    ref={suggestWrapRef}
                    className="relative flex flex-col flex-1 min-w-0"
                  >
                    <div className="flex items-center gap-2 flex-1 border border-gray-200 rounded-full px-3 sm:px-4 min-w-0">
                      <Search
                        size={18}
                        className="text-[#097FE6] flex-shrink-0 sm:w-5 sm:h-5"
                      />
                      <input
                        type="text"
                        name="searchValue"
                        autoComplete="off"
                        value={searchValue}
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          localSave(e);
                        }}
                        onKeyDown={onSearchKeyDown}
                        placeholder="Search Suburb, Postcode or Region"
                        className="w-full min-w-0 outline-none text-xs sm:text-sm text-black py-2.5 sm:py-3"
                      />
                      {/* <button
                        type="button"
                        title="Use my location"
                        disabled={locationBusy}
                        onClick={() => runLocationSearch()}
                        className="flex-shrink-0 p-2 rounded-full text-[#097FE6] hover:bg-gray-100 disabled:opacity-50"
                      >
                        <MapPin size={18} />
                      </button> */}
                      <div
                        onClick={() => setIsOpen(true)}
                        className="flex items-center gap-2 pl-2 sm:pl-4 text-sm py-3 border-l border-gray-200 cursor-pointer flex-shrink-0"
                      >
                        <ListFilter className="flex sm:hidden mr-1" size={14} />
                        <SlidersHorizontal className="hidden sm:flex" size={14} />
                        <div className="hidden sm:flex">Filters</div>
                      </div>
                    </div>
                    {suggestions.length > 0 && (
                      <ul
                        className="filter-screen-suggestions absolute left-0 right-0 top-full mt-1 z-[100] max-h-64 overflow-y-auto rounded-xl border border-[#E5E7EB] bg-white py-1 shadow-lg"
                        role="listbox"
                      >
                        {suggestions.map((p, idx) => {
                          const isHighlighted = idx === highlightIndex;
                          return (
                            <li key={p.place_id}>
                              <button
                                type="button"
                                role="option"
                                aria-selected={isHighlighted}
                                className={`filter-screen-suggestion-item w-full flex items-start gap-2.5 px-3.5 py-2.5 text-left text-xs sm:text-sm text-[#000929] transition-colors ${isHighlighted
                                  ? "bg-[#EFF6FF]"
                                  : "hover:bg-[#EFF6FF]"
                                  }`}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseEnter={() => setHighlightIndex(idx)}
                                onClick={() => applySuggestion(p)}
                              >
                                <MapPin
                                  size={18}
                                  strokeWidth={2}
                                  className={`flex-shrink-0 mt-0.5 ${isHighlighted
                                    ? "text-[#097FE6]"
                                    : "text-[#6B7280]"
                                    }`}
                                  aria-hidden
                                />
                                <span className="leading-snug">
                                  {p.description}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                  <motion.button
                    type="button"
                    onClick={searchBtn}
                    disabled={locationBusy || searchValue.length === 0}
                    whileHover="hover"
                    whileTap={{ scale: 0.97 }}
                    initial="rest"
                    animate="rest"
                    variants={{
                      rest: { y: 0 },
                      hover: { y: -1 },
                    }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="flex justify-center items-center gap-2 px-5 sm:px-6 py-3 rounded-full bg-[#097FE6] text-white text-sm font-medium cursor-pointer flex-shrink-0 disabled:opacity-60"
                  >
                    <span>{locationBusy ? "Locating…" : "Search"}</span>
                    <motion.span
                      variants={{
                        rest: { x: 0, opacity: 0.8 },
                        hover: { x: 4, opacity: 1 },
                      }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                    >
                      <ChevronRight className="w-5 h-5 text-white" />
                    </motion.span>
                  </motion.button>
                </div>
              </>
            )}

            {isActive === TabData[3] && (
              <>
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full min-w-0">
                  <div className="flex items-center gap-2 flex-1 border border-gray-200 rounded-full px-3 sm:px-4 min-w-0">
                    <Search
                      size={18}
                      className="text-[#097FE6] flex-shrink-0 sm:w-5 sm:h-5"
                    />
                    <input
                      type="text"
                      name="searchAgent"
                      value={searchAgent}
                      onChange={(e) => setSearchAgent(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          navigate(`/find-agents/${searchAgent}`);
                      }}
                      placeholder="Search Agent"
                      className="w-full min-w-0 outline-none text-xs sm:text-sm text-gray-700 py-2.5 sm:py-3"
                    />
                  </div>
                  <button
                    onClick={() => navigate(`/find-agents/${searchAgent}`)}
                    disabled={searchAgent.length === 0}
                    className="w-full sm:w-auto flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full bg-[#097FE6] text-white text-xs sm:text-sm font-medium flex-shrink-0"
                  >
                    Search
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <FilterScreenModel
        setIsOpen={setIsOpen}
        IsOpen={IsOpen}
        currentTab={isActive}
      />
    </>
  );
};

export default FilterScreen;
